'use client';

import Loader from '@pickleballinc/features/src/Loader/Loader';
import { Tooltip } from '@pickleballinc/react-ui';
import { Button } from '@pickleballinc/ui/components/ui/button';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import { PlayerSelect } from '@/components/atoms';
import { PlayerType } from '@/components/constants/enums';
import { useScoreCard } from '@/contexts/ScoreCardProvider';
import { SubMatch, TeamLeaguePlayerInfo } from '@/entities';
import { matchKeys } from '@/hooks/matches/matchKeys';
import { useChangePlayerMutation } from '@/hooks/matches/useChangePlayer';
import { LineupEntryOrder } from '@/infrastructure/network/dto/SeasonDivisionPool';
import { DateUtil } from '@/utils/date';
import { filterPlayersByGenderRule } from '@/utils/helpers';

interface LineupCardProps {
	match: SubMatch;
	awayTeamPlayers: TeamLeaguePlayerInfo[];
	homeTeamPlayers: TeamLeaguePlayerInfo[];
	homeTeamTitle: string;
	awayTeamTitle: string;
	className?: string;
	showOponentLineup?: boolean;
	teamCaptain?: 'home' | 'away' | undefined;
	lineupEntryOrder?: LineupEntryOrder;
	canCaptainSetLineup?: boolean;
}

export const LineupCard = ({
	match,
	awayTeamPlayers,
	homeTeamPlayers,
	homeTeamTitle,
	awayTeamTitle,
	className = '',
	showOponentLineup = false,
	teamCaptain = undefined,
	lineupEntryOrder,
	canCaptainSetLineup = false
}: LineupCardProps) => {
	const queryClient = useQueryClient();

	const { updateMatchLineupStatus, updateSelectedPlayers, matchupData, getSelectedPlayers, haveSelectedPlayersChanged } = useScoreCard();
	const homeTeamEnterLineupFirst = lineupEntryOrder === LineupEntryOrder.HOME_TEAM_FIRST_LINEUP_ENTRY;
	const awayTeamEnterLineupFirst = lineupEntryOrder === LineupEntryOrder.AWAY_TEAM_FIRST_LINEUP_ENTRY;
	const hasSelectedPlayersChanged = haveSelectedPlayersChanged(match.matchUuid);

	const { ianaTzIdentifier, abbreviation } = match.timezone;

	const formattedStartDate = match.matchPlannedStart
		? DateUtil.formatUTCInTimezone(match.matchPlannedStart, 'MMM D, YYYY hh:mm A', ianaTzIdentifier)
		: '';
	const formattedHomePlayers = filterPlayersByGenderRule(homeTeamPlayers, match.playerGroupTitle);
	const formattedAwayPlayers = filterPlayersByGenderRule(awayTeamPlayers, match.playerGroupTitle);

	const isDoubles = match.formatTitle?.toLowerCase().includes('doubles') || false;

	const { mutate: changePlayerMutation, isPending: isChangingPlayer } = useChangePlayerMutation({
		onSuccessCallback: () => {
			queryClient.invalidateQueries({ queryKey: matchKeys.matches(matchupData.uuid) });
			updateMatchLineupStatus(match.matchUuid, true);
		}
	});

	const handlePlayerSelect = (selectedPlayerId: string | number, playerType: PlayerType, side: 'home' | 'away') => {
		const isHome = side === 'home';
		const playerTypeMap = {
			[PlayerType.PLAYER_ONE]: isHome ? 'teamOnePlayerOneUuid' : 'teamTwoPlayerOneUuid',
			[PlayerType.PLAYER_TWO]: isHome ? 'teamOnePlayerTwoUuid' : 'teamTwoPlayerTwoUuid'
		};

		updateSelectedPlayers(match.matchUuid, playerTypeMap[playerType], selectedPlayerId.toString());
	};

	const handleOnSaveLineup = () => {
		const selectedPlayers = getSelectedPlayers(match.matchUuid);

		if (
			isDoubles &&
			typeof selectedPlayers.teamOnePlayerOneUuid === 'string' &&
			selectedPlayers.teamOnePlayerOneUuid === selectedPlayers.teamOnePlayerTwoUuid
		) {
			toast.error('You cannot select the same player twice for the home team.');
			return;
		}

		if (
			isDoubles &&
			typeof selectedPlayers.teamTwoPlayerOneUuid === 'string' &&
			selectedPlayers.teamTwoPlayerOneUuid === selectedPlayers.teamTwoPlayerTwoUuid
		) {
			toast.error('You cannot select the same player twice for the away team.');
			return;
		}

		const validPlayers = {
			...(selectedPlayers.teamOnePlayerOneUuid && { swappedOutButPlayedTeam1UserId1: selectedPlayers.teamOnePlayerOneUuid }),
			...(selectedPlayers.teamOnePlayerTwoUuid && { swappedOutButPlayedTeam1UserId2: selectedPlayers.teamOnePlayerTwoUuid }),
			...(selectedPlayers.teamTwoPlayerOneUuid && { swappedOutButPlayedTeam2UserId1: selectedPlayers.teamTwoPlayerOneUuid }),
			...(selectedPlayers.teamTwoPlayerTwoUuid && { swappedOutButPlayedTeam2UserId2: selectedPlayers.teamTwoPlayerTwoUuid })
		};

		changePlayerMutation({
			matchId: match.matchUuid,
			updateData: validPlayers
		});
	};

	const getPlayerByUuid = (players: TeamLeaguePlayerInfo[], playerUuid: string | null) => players.find((player) => player.userUuid === playerUuid);

	const checkHomeTeamLineup = teamCaptain === 'home' && awayTeamEnterLineupFirst && !match.teamTwoPlayerOneUuid && !match.teamTwoPlayerTwoUuid;
	const checkAwayTeamLineup = teamCaptain === 'away' && homeTeamEnterLineupFirst && !match.teamOnePlayerOneUuid && !match.teamOnePlayerTwoUuid;

	const toolTipContent = (() => {
		let message = null;
		if (checkHomeTeamLineup) {
			message = ` Waiting for away team to select their lineups.`;
		}

		if (checkAwayTeamLineup) {
			message = ` Waiting for home team to select their lineups.`;
		}

		return message;
	})();

	return (
		<div className={`relative bg-white border border-[#EF6820] rounded-lg p-4 w-full lg:max-w-[600px] ${className}`}>
			{isChangingPlayer ? (
				<Loader backgroundColor="bg-[#EF6820]" containerClassName="w-full h-full absolute rounded-md z-10 opacity-90" isComponent />
			) : null}
			<div className="flex justify-between mb-3">
				<h3 className="text-lg font-medium text-gray-900 mb-2 sm:mb-0">{match.eventTitle || 'Match'}</h3>
				{canCaptainSetLineup ? (
					<Tooltip placement="top" key={toolTipContent}>
						<Tooltip.Trigger asChild>
							<div className="inline-block">
								<Button
									onClick={handleOnSaveLineup}
									variant="outline"
									className="text-sm text-gray-800 w-fit"
									disabled={isChangingPlayer || !hasSelectedPlayersChanged || checkHomeTeamLineup || checkAwayTeamLineup}
								>
									Save lineup
								</Button>
							</div>
						</Tooltip.Trigger>
						{toolTipContent ? (
							<Tooltip.Content>
								<p>{toolTipContent}</p>
							</Tooltip.Content>
						) : null}
					</Tooltip>
				) : null}
			</div>

			<div className="text-sm text-gray-500 mb-4">
				{formattedStartDate ? (
					<>
						<span>
							{formattedStartDate} {abbreviation}
						</span>
						{match.courtTitle ? <span className="text-gray-400 mx-1">|</span> : null}
					</>
				) : null}
				{match.courtTitle ? <span>Court: {match.courtTitle}</span> : null}
				<div> {match.scoreFormatShorthand}</div>
			</div>

			<div className="mb-6">
				<h4 className="text-lg font-bold text-gray-900 text-center mb-4">{awayTeamTitle} (A)</h4>
				<div className="flex flex-col gap-4 sm:flex-row sm:gap-8">
					<PlayerSelect
						players={formattedAwayPlayers}
						onSelect={(selectedPlayerId) => handlePlayerSelect(selectedPlayerId.toString(), PlayerType.PLAYER_ONE, 'away')}
						playerLabel="Player 1"
						placeholder="Select Player 1"
						selectedPlayer={getPlayerByUuid(formattedAwayPlayers, match.teamTwoPlayerOneUuid)}
						isLocked={!canCaptainSetLineup || teamCaptain === 'home'}
						isBlured={!showOponentLineup}
					/>
					{isDoubles ? (
						<PlayerSelect
							players={formattedAwayPlayers}
							onSelect={(selectedPlayerId) => handlePlayerSelect(selectedPlayerId.toString(), PlayerType.PLAYER_TWO, 'away')}
							playerLabel="Player 2"
							placeholder="Select Player 2"
							selectedPlayer={getPlayerByUuid(formattedAwayPlayers, match.teamTwoPlayerTwoUuid)}
							isLocked={!canCaptainSetLineup || teamCaptain === 'home'}
							isBlured={!showOponentLineup}
						/>
					) : null}
				</div>
			</div>

			<div className="flex items-center justify-center mb-6">
				<div className="flex-1 h-px bg-gray-300" />
				<span className="px-4 text-gray-500 font-medium">vs</span>
				<div className="flex-1 h-px bg-gray-300" />
			</div>

			<div>
				<h4 className="text-lg font-bold text-gray-900 text-center mb-4">{homeTeamTitle} (H)</h4>
				<div className="flex flex-col gap-4 sm:flex-row sm:gap-8">
					<PlayerSelect
						players={formattedHomePlayers}
						onSelect={(selectedPlayerId) => handlePlayerSelect(selectedPlayerId.toString(), PlayerType.PLAYER_ONE, 'home')}
						playerLabel="Player 1"
						placeholder="Select Player 1"
						selectedPlayer={getPlayerByUuid(formattedHomePlayers, match.teamOnePlayerOneUuid)}
						isLocked={!canCaptainSetLineup || teamCaptain === 'away'}
						isBlured={!showOponentLineup}
					/>
					{isDoubles ? (
						<PlayerSelect
							players={formattedHomePlayers}
							onSelect={(selectedPlayerId) => handlePlayerSelect(selectedPlayerId.toString(), PlayerType.PLAYER_TWO, 'home')}
							playerLabel="Player 2"
							placeholder="Select Player 2"
							selectedPlayer={getPlayerByUuid(formattedHomePlayers, match.teamOnePlayerTwoUuid)}
							isLocked={!canCaptainSetLineup || teamCaptain === 'away'}
							isBlured={!showOponentLineup}
						/>
					) : null}
				</div>
			</div>
		</div>
	);
};
